import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import type { RoleOrgType } from '../../auth/role-templates';
import {
  HANDBOOK_INTRO,
  HANDBOOK_ROLE_PRESETS,
  HANDBOOK_UI_GATING,
  PERMISSIONS_HANDBOOK_ARTICLE_ID,
  handbookEntitiesForOrgType,
} from '../../constants/permissions-handbook';
import { actionDisplayName } from '../../auth/permission-catalog';

interface PermissionsReferenceDialogProps {
  open: boolean;
  onClose: () => void;
  orgType: RoleOrgType | null;
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5, pb: 1 }}>{children}</Box>;
}

const PermissionsReferenceDialog: React.FC<PermissionsReferenceDialogProps> = ({
  open,
  onClose,
  orgType,
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const entities = useMemo(
    () => (orgType ? handbookEntitiesForOrgType(orgType) : []),
    [orgType],
  );

  const uiByArea = useMemo(() => {
    const map = new Map<string, typeof HANDBOOK_UI_GATING>();
    for (const item of HANDBOOK_UI_GATING) {
      const list = map.get(item.area) ?? [];
      list.push(item);
      map.set(item.area, list);
    }
    return [...map.entries()];
  }, []);

  const openInHelp = () => {
    onClose();
    navigate(`/knowledge-base/${PERMISSIONS_HANDBOOK_ARTICLE_ID}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: 'min(1200px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 48px)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 0,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={700} lineHeight={1.25}>
                Permission reference
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
                {HANDBOOK_INTRO}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ mt: -0.5 }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mt: 2 }}
          >
            <Tab
              icon={<CategoryOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Areas & access"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab
              icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Role presets"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab
              icon={<AppsOutlinedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="App features"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
          </Tabs>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 0 }}>
          <TabPanel value={tab} index={0}>
            {!orgType ? (
              <Card variant="outlined">
                <CardContent sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Select an organization to see what each area controls for your business type.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {entities.map((entity) => (
                  <Card key={entity.id} variant="outlined">
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={{ xs: 2, md: 3 }}
                        alignItems={{ md: 'flex-start' }}
                      >
                        <Box sx={{ width: { md: 220 }, flexShrink: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
                            {entity.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, lineHeight: 1.5 }}
                          >
                            {entity.summary}
                          </Typography>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {entity.grants.map((grant) => (
                              <Box
                                key={`${entity.id}-${grant.action}`}
                                sx={{
                                  flex: '1 1 200px',
                                  maxWidth: { sm: 'calc(50% - 8px)', lg: 'calc(33.33% - 8px)' },
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.default',
                                }}
                              >
                                <Chip
                                  label={actionDisplayName(grant.action)}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontWeight: 600, mb: 0.75 }}
                                />
                                <Typography variant="body2" color="text.secondary" lineHeight={1.45}>
                                  {grant.description}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
              Ready-made access levels you can pick on the user edit page. You can always switch to
              Advanced permissions to adjust individual areas.
            </Typography>
            <Grid container spacing={2}>
              {HANDBOOK_ROLE_PRESETS.map((preset) => (
                <Grid item xs={12} sm={6} key={preset.role}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.25 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            flexShrink: 0,
                          }}
                        >
                          <BadgeOutlinedIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
                            {preset.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {preset.typicalPersona}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.55}>
                        {preset.summary}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
              What employees need before they can use common parts of the app.
            </Typography>
            <Stack spacing={2.5}>
              {uiByArea.map(([area, items]) => (
                <Box key={area}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1, letterSpacing: 1 }}
                  >
                    {area}
                  </Typography>
                  <Stack spacing={1.5}>
                    {items.map((item) => (
                      <Card key={item.feature} variant="outlined">
                        <CardContent
                          sx={{
                            py: 2,
                            px: 2.5,
                            '&:last-child': { pb: 2 },
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={{ xs: 0.75, sm: 3 }}
                            alignItems={{ sm: 'center' }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ minWidth: { sm: 200 }, flexShrink: 0 }}
                            >
                              {item.feature}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" lineHeight={1.55}>
                              {item.description}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Button
          size="small"
          startIcon={<MenuBookOutlinedIcon />}
          onClick={openInHelp}
          sx={{ textTransform: 'none' }}
        >
          View in Help center
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsReferenceDialog;
